import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, CheckCircle2, UserPlus, Wheat, Grape, X, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";

function ForgotPasswordModal({ onClose }) {
  const [step, setStep] = useState("email"); // email | otp | done
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const sendOtp = async () => {
    if (!email.trim()) { toast.error("कृपया ई-मेल प्रविष्ट करा."); return; }
    setBusy(true);
    try {
      await api.resetRequestOtp({ email: email.trim() });
      toast.success("जर या ई-मेलने खाते असेल, तर OTP पाठवला आहे.");
      setStep("otp");
    } catch (e) { toast.error(e.message || "त्रुटी आली."); }
    setBusy(false);
  };

  const resetPw = async () => {
    if (!code.trim()) { toast.error("OTP प्रविष्ट करा."); return; }
    if (pw.length < 6) { toast.error("पासवर्ड किमान ६ अक्षरांचा असावा."); return; }
    if (pw !== pw2) { toast.error("दोन्ही पासवर्ड जुळत नाहीत."); return; }
    setBusy(true);
    try {
      const v = await api.resetVerify({ email: email.trim(), code: code.trim() });
      await api.resetComplete({ email: email.trim(), ticket: v.ticket, password: pw });
      setStep("done");
    } catch (e) { toast.error(e.message || "OTP चुकीचा किंवा मुदत संपली."); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="forgot-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-[18px] font-bold text-[#1b5e3f]">पासवर्ड विसरलात?</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="forgot-close"><X className="h-5 w-5" /></button>
        </div>

        {step === "email" && (
          <div className="space-y-4">
            <p className="text-[13.5px] text-slate-600">आपल्या नोंदणीकृत ई-मेलवर OTP पाठवला जाईल. त्याद्वारे नवीन पासवर्ड तयार करा.</p>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ई-मेल" data-testid="forgot-email"
              className="w-full rounded-lg border border-slate-300 px-4 py-3 text-[15px] outline-none focus:border-[#1b5e3f]" />
            <button onClick={sendOtp} disabled={busy} data-testid="forgot-send-otp"
              className="w-full rounded-full bg-[#1b5e3f] py-3 text-[15px] font-semibold text-white hover:bg-[#144d33] disabled:opacity-60">
              {busy ? "पाठवत आहे..." : "OTP पाठवा"}
            </button>
          </div>
        )}

        {step === "otp" && (
          <div className="space-y-3">
            <p className="text-[13.5px] text-slate-600"><b>{email}</b> वर पाठवलेला OTP व नवीन पासवर्ड प्रविष्ट करा.</p>
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="OTP" data-testid="forgot-otp"
              className="w-full rounded-lg border border-slate-300 px-4 py-3 text-[15px] tracking-widest outline-none focus:border-[#1b5e3f]" />
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="नवीन पासवर्ड (किमान ६ अक्षरे)" data-testid="forgot-new-pw"
              className="w-full rounded-lg border border-slate-300 px-4 py-3 text-[15px] outline-none focus:border-[#1b5e3f]" />
            <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="नवीन पासवर्ड पुन्हा" data-testid="forgot-new-pw2"
              className="w-full rounded-lg border border-slate-300 px-4 py-3 text-[15px] outline-none focus:border-[#1b5e3f]" />
            <button onClick={resetPw} disabled={busy} data-testid="forgot-reset"
              className="w-full rounded-full bg-[#1b5e3f] py-3 text-[15px] font-semibold text-white hover:bg-[#144d33] disabled:opacity-60">
              {busy ? "बदलत आहे..." : "पासवर्ड बदला"}
            </button>
            <button onClick={sendOtp} disabled={busy} className="w-full text-center text-[13px] font-semibold text-slate-500 hover:text-[#1b5e3f]">OTP पुन्हा पाठवा</button>
          </div>
        )}

        {step === "done" && (
          <div className="space-y-4 text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
            <p className="text-[15px] font-semibold text-slate-700">पासवर्ड यशस्वीरित्या बदलला!</p>
            <p className="text-[13.5px] text-slate-500">आता आपल्या नवीन पासवर्डने लॉगिन करा.</p>
            <button onClick={onClose} data-testid="forgot-done"
              className="w-full rounded-full bg-[#1b5e3f] py-3 text-[15px] font-semibold text-white hover:bg-[#144d33]">लॉगिन करा</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { login } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [dl, setDl] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.getDeadline().then((d) => { if (alive) setDl(d); }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const notStarted = !!dl?.notStarted;
  const startStr = (() => {
    if (!dl?.startDate) return "";
    const d = new Date(dl.startDate);
    const MON = ["जानेवारी", "फेब्रुवारी", "मार्च", "एप्रिल", "मे", "जून", "जुलै", "ऑगस्ट", "सप्टेंबर", "ऑक्टोबर", "नोव्हेंबर", "डिसेंबर"];
    let h = d.getHours(); const m = d.getMinutes(); const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12;
    return `${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()}, ${h}:${m < 10 ? "0" + m : m} ${ap}`;
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError("कृपया ई-मेल व पासवर्ड प्रविष्ट करा.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      navigate(u.role === "admin" ? "/admin" : "/apply");
    } catch (err) {
      setError(err.message || "लॉगिन अयशस्वी.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f6f2e6]">
      {forgot && <ForgotPasswordModal onClose={() => setForgot(false)} />}
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-6 py-10 md:grid-cols-2 md:py-20">
        {/* form card (right on desktop) */}
        <div className="order-2 rounded-[1.75rem] border border-[#e6dcc4] bg-white p-8 shadow-[0_16px_50px_rgba(27,94,63,0.10)] sm:p-10">
          <div className="mb-2 flex items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#1b5e3f] text-white">
              <Wheat className="h-5 w-5" strokeWidth={2} />
            </span>
            <span className="font-latin text-[15px] font-bold text-[#1b5e3f]">ASSK Sangli</span>
          </div>
          <h1 className="font-head text-[34px] font-medium text-[#1b5e3f]">Login</h1>
          <p className="mt-1 text-[15px] text-[#6b7a60]">Access your ASSK applicant dashboard</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label className="mb-1.5 block text-[13px] font-bold tracking-wide text-slate-500">
                USERNAME (E-MAIL) <span className="text-slate-400">*</span>
              </label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[15px] outline-none focus:border-[#1b5e3f] focus:ring-2 focus:ring-[#1b5e3f]/20"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-[13px] font-bold tracking-wide text-slate-500">
                PASSWORD <span className="text-slate-400">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="w-full rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 pr-11 text-[15px] outline-none focus:border-[#1b5e3f] focus:ring-2 focus:ring-[#1b5e3f]/20"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {error && <p className="text-[13px] text-red-600" data-testid="login-error">{error}</p>}

            <label className="flex items-center gap-2 text-[14px] text-slate-600">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 rounded border-slate-300 accent-[#1b5e3f]" />
              Remember me
            </label>

            <button type="submit" disabled={busy} data-testid="login-submit" className="w-full rounded-full bg-[#1b5e3f] py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-[#144d33] disabled:opacity-60">
              {busy ? "Signing in..." : "Sign In to Portal"}
            </button>
            <button type="button" onClick={() => setForgot(true)} data-testid="forgot-open" className="block w-full text-center text-[14px] font-semibold text-slate-700 hover:text-[#1b5e3f]">
              Forgot Password?
            </button>
            <button type="button" onClick={() => navigate("/officer")} className="block w-full text-center text-[13px] font-semibold text-[#1b5e3f] hover:underline">
              अधिकारी लॉगिन (Collector / RDC / SDO / Tehsildar) →
            </button>
          </form>
        </div>

        {/* promo panel (left on desktop) — matches official landing card */}
        <div className="relative order-1 flex flex-col justify-center overflow-hidden rounded-[1.75rem] bg-gradient-to-br from-[#2f5233] to-[#1f3a24] p-9 text-white md:p-11" data-testid="promo-card">
          <div className="pointer-events-none absolute -right-6 -top-6 opacity-10">
            <Wheat className="h-40 w-40" strokeWidth={1.2} />
          </div>
          <div className="pointer-events-none absolute bottom-4 right-6 opacity-10">
            <Grape className="h-24 w-24" strokeWidth={1.2} />
          </div>

          {/* office header, gold underline */}
          <div className="relative mb-6">
            <span className="inline-block border-b-[3px] border-[#d4a017] pb-1.5 text-[21px] font-medium tracking-wide text-white">
              जिल्हाधिकारी कार्यालय, सांगली
            </span>
          </div>

          <span className="relative mb-5 w-fit rounded-full bg-[#d4a017] px-4 py-1.5 text-[13px] font-medium text-[#3d2b00]">नवीन अर्जदार</span>

          <p className="relative mb-1 text-[12px] text-[#cfe0cf]">आपले सरकार सेवा केंद्र (ASSK)</p>
          <h2 className="font-head relative mb-3 text-[34px] font-medium leading-[1.25] text-white">नवीन ASSK अर्ज सुरू करा</h2>
          <p className="relative mb-6 max-w-md text-[15px] leading-[1.7] text-[#dce8dc]">
            सांगली जिल्ह्यासाठी ASSK कार्यक्रमात सहभागी व्हा.<br />
            आपला अर्ज टप्प्याटप्प्याने पूर्ण करण्यासाठी वैयक्तिक डॅशबोर्ड मिळवा — जिथे थांबाल तिथून पुढे सुरू ठेवता येईल.
          </p>

          <div className="relative mb-6 flex max-w-lg items-start gap-3.5 rounded-xl border border-white/[0.18] bg-white/[0.08] p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[#d4a017] text-[#3d2b00]">
              <UserPlus className="h-5 w-5" />
            </div>
            <div>
              <p className="text-[15px] font-medium text-white">पहिली पायरी: आपले खाते तयार करा</p>
              <p className="text-[13px] text-[#cfe0cf]">सुरुवात करण्यासाठी आपली मूलभूत माहिती नोंदवा</p>
            </div>
          </div>

          <div className="relative mb-4 flex flex-wrap items-center gap-3.5">
            <button onClick={() => navigate("/register")} disabled={notStarted} data-testid="promo-start-btn"
              className="rounded-[10px] bg-[#d4a017] px-6 py-3 text-[15px] font-medium text-[#3d2b00] transition-colors hover:bg-[#c2900f] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#d4a017]">
              नवीन अर्ज सुरू करा
            </button>
            <a href="/jahirat-ati-sharti.pdf" target="_blank" rel="noopener noreferrer" data-testid="promo-ad-btn"
              className="inline-flex items-center gap-2 rounded-[10px] bg-white px-6 py-2.5 text-[14px] font-medium text-[#1f3a24] transition-colors hover:bg-[#f0f0f0]">
              <Megaphone className="h-4 w-4" /> जाहिरात पहा
            </a>
          </div>
          {notStarted && (
            <p className="relative mb-4 max-w-md rounded-lg border border-[#d4a017]/40 bg-[#d4a017]/[0.12] px-3.5 py-2.5 text-[13px] font-medium text-[#f0cf6b]" data-testid="not-started-notice">
              अर्ज स्वीकारण्यास <b>{startStr}</b> पासून सुरुवात होईल. तोपर्यंत नवीन अर्ज सुरू करता येणार नाही.
            </p>
          )}

          <p className="relative text-[13px] text-[#cfe0cf]">
            आधीच अर्ज केला आहे?{" "}
            <button onClick={() => document.querySelector('[data-testid="login-email"]')?.focus()} className="font-semibold text-[#f0cf6b] hover:underline">
              शेजारी लॉगिन करा
            </button>
          </p>

          <div className="relative mt-5 flex flex-wrap justify-between gap-2 border-t border-white/[0.15] pt-3.5 text-[12px] text-[#a9bfa9]">
            <span>जाहीरनामा २०२६ · शासन निर्णय दि. ०९-०७-२०२६</span>
            <span>अधिक माहितीसाठी आपल्या तहसील कार्यालयात संपर्क करा</span>
          </div>
        </div>
      </div>
    </div>
  );
}
